import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useClientSettings, useClients } from '@/hooks/useClientSettings';
import { usePermissions } from '@/hooks/usePermissions';
import { 
  Building2, 
  Shield, 
  Clock, 
  Send, 
  FileText, 
  AlertTriangle,
  Save,
  Plus,
  Settings2,
  Lock,
  Rocket,
  Stethoscope,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ClientSpecialtiesPanel } from '@/components/admin/ClientSpecialtiesPanel';

export default function ClientSettingsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { clientId, isSuperAdmin, hasPermission } = usePermissions();
  const { clients, createClient, isCreating } = useClients();
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const { settings, isUpdating } = useClientSettings(selectedClientId);

  // Set initial client ID when available
  useEffect(() => {
    if (clientId && !selectedClientId) {
      setSelectedClientId(clientId);
    }
  }, [clientId, selectedClientId]);

  const updateSettings = (_updates: Record<string, unknown>) => {
    // Placeholder - will use the hook's updateSettings
  };

  const [formState, setFormState] = useState({
    default_epic_pushback_destination: 'task',
    default_submission_lane_priority: ['portal', 'fax', 'phone'],
    sla_hours_default: 336,
    urgent_sla_hours_default: 72,
    follow_up_cadence_hours_default: 48,
    hospital_admin_can_export: false,
    phi_masking_enabled: true,
    sensitive_data_logging_enabled: true,
    max_retry_attempts: 3,
    retry_backoff_multiplier: 2.0,
  });

  // Update form state when settings load
  useEffect(() => {
    if (settings) {
      setFormState({
        default_epic_pushback_destination: settings.default_epic_pushback_destination || 'task',
        default_submission_lane_priority: settings.default_submission_lane_priority || ['portal', 'fax', 'phone'],
        sla_hours_default: settings.sla_hours_default || 336,
        urgent_sla_hours_default: settings.urgent_sla_hours_default || 72,
        follow_up_cadence_hours_default: settings.follow_up_cadence_hours_default || 48,
        hospital_admin_can_export: settings.hospital_admin_can_export || false,
        phi_masking_enabled: settings.phi_masking_enabled ?? true,
        sensitive_data_logging_enabled: settings.sensitive_data_logging_enabled ?? true,
        max_retry_attempts: settings.max_retry_attempts || 3,
        retry_backoff_multiplier: settings.retry_backoff_multiplier || 2.0,
      });
    }
  }, [settings]);

  const [newClient, setNewClient] = useState<{
    client_name: string;
    client_type: 'HOSPITAL' | 'SURGICAL_PRACTICE' | 'HEALTH_SYSTEM';
    primary_state: string;
  }>({
    client_name: '',
    client_type: 'SURGICAL_PRACTICE',
    primary_state: '',
  });

  const canEdit = isSuperAdmin() || hasPermission('config.payer.edit');

  const handleSave = () => {
    if (!selectedClientId || !canEdit) return;

    updateSettings(formState);
    toast({
      title: 'Settings saved',
      description: 'Client settings have been updated successfully.',
    });
  };

  const handleCreateClient = () => {
    if (!newClient.client_name.trim()) {
      toast({
        title: 'Error',
        description: 'Client name is required',
        variant: 'destructive',
      });
      return;
    }

    createClient({
      client_name: newClient.client_name,
      client_type: newClient.client_type,
      primary_state: newClient.primary_state || undefined,
    });
    
    setNewClient({ client_name: '', client_type: 'SURGICAL_PRACTICE', primary_state: '' });
    toast({
      title: 'Client created',
      description: 'New client has been created successfully.',
    });
  };

  if (!canEdit) {
    return (
      <OpsLayout>
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-2">Client Settings</h1>
          <p className="text-muted-foreground mb-6">Access denied</p>
          <Card>
            <CardContent className="py-12 text-center">
              <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">You don't have permission to view client settings.</p>
            </CardContent>
          </Card>
        </div>
      </OpsLayout>
    );
  }

  return (
    <OpsLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-2">Client Settings</h1>
        <p className="text-muted-foreground mb-6">Configure tenant-specific settings and defaults</p>
        <div className="space-y-6">
        {/* Client Selector (Super Admin only) */}
        {isSuperAdmin() && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Client Selection
              </CardTitle>
              <CardDescription>
                Select a client to configure or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="client-select">Select Client</Label>
                  <Select
                    value={selectedClientId}
                    onValueChange={setSelectedClientId}
                  >
                    <SelectTrigger id="client-select">
                      <SelectValue placeholder="Select a client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.client_name}
                          <Badge variant="outline" className="ml-2 text-xs">
                            {client.client_type}
                          </Badge>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Create New Client</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="new-client-name">Client Name</Label>
                    <Input
                      id="new-client-name"
                      value={newClient.client_name}
                      onChange={e => setNewClient(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="e.g., Cincinnati Orthopedic Associates"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-client-type">Type</Label>
                    <Select
                      value={newClient.client_type}
                      onValueChange={v => setNewClient(prev => ({ ...prev, client_type: v as 'HOSPITAL' | 'SURGICAL_PRACTICE' | 'HEALTH_SYSTEM' }))}
                    >
                      <SelectTrigger id="new-client-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SURGICAL_PRACTICE">Surgical Practice</SelectItem>
                        <SelectItem value="HOSPITAL">Hospital</SelectItem>
                        <SelectItem value="HEALTH_SYSTEM">Health System</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="new-client-state">Primary State</Label>
                    <Input
                      id="new-client-state"
                      value={newClient.primary_state}
                      onChange={e => setNewClient(prev => ({ ...prev, primary_state: e.target.value }))}
                      placeholder="e.g., OH"
                      maxLength={2}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateClient} disabled={isCreating}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Client
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/admin/onboarding')}
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Onboarding Wizard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tabs */}
        {selectedClientId && (
          <Tabs defaultValue="general" className="space-y-4">
            <TabsList>
              <TabsTrigger value="general">
                <Settings2 className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger value="submissions">
                <Send className="w-4 h-4 mr-2" />
                Submissions
              </TabsTrigger>
              <TabsTrigger value="sla">
                <Clock className="w-4 h-4 mr-2" />
                SLA & Follow-ups
              </TabsTrigger>
              <TabsTrigger value="security">
                <Shield className="w-4 h-4 mr-2" />
                Security & Privacy
              </TabsTrigger>
              <TabsTrigger value="exports">
                <FileText className="w-4 h-4 mr-2" />
                Exports
              </TabsTrigger>
              <TabsTrigger value="specialties">
                <Stethoscope className="w-4 h-4 mr-2" />
                Specialties
              </TabsTrigger>
            </TabsList>

            <TabsContent value="specialties">
              <ClientSpecialtiesPanel clientId={selectedClientId} canEdit={canEdit} />
            </TabsContent>

            <TabsContent value="general">
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>
                    Core configuration for this client
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="epic-destination">Default Epic Pushback Destination</Label>
                    <Select
                      value={formState.default_epic_pushback_destination}
                      onValueChange={v => setFormState(prev => ({ ...prev, default_epic_pushback_destination: v }))}
                    >
                      <SelectTrigger id="epic-destination">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="task">Epic Task</SelectItem>
                        <SelectItem value="inbasket">InBasket Message</SelectItem>
                        <SelectItem value="chart_note">Chart Note</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Where authorization updates are pushed back to Epic
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="submissions">
              <Card>
                <CardHeader>
                  <CardTitle>Submission Lane Priorities</CardTitle>
                  <CardDescription>
                    Default submission lane order when payer-specific rules don't apply
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {formState.default_submission_lane_priority.map((lane, idx) => (
                        <Badge key={lane} variant="secondary" className="text-sm">
                          {idx + 1}. {lane.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Lanes are attempted in order: Portal → Fax → Phone
                    </p>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="max-retry">Max Retry Attempts</Label>
                      <Input
                        id="max-retry"
                        type="number"
                        min={1}
                        max={10}
                        value={formState.max_retry_attempts}
                        onChange={e => setFormState(prev => ({ ...prev, max_retry_attempts: parseInt(e.target.value) || 3 }))}
                      />
                      <p className="text-sm text-muted-foreground">
                        Number of retry attempts before marking as failed
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="backoff">Retry Backoff Multiplier</Label>
                      <Input
                        id="backoff"
                        type="number"
                        step="0.5"
                        min={1}
                        max={5}
                        value={formState.retry_backoff_multiplier}
                        onChange={e => setFormState(prev => ({ ...prev, retry_backoff_multiplier: parseFloat(e.target.value) || 2.0 }))}
                      />
                      <p className="text-sm text-muted-foreground">
                        Exponential backoff multiplier (e.g., 2x = 1h, 2h, 4h...)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sla">
              <Card>
                <CardHeader>
                  <CardTitle>SLA & Follow-up Defaults</CardTitle>
                  <CardDescription>
                    Default timing settings when payer-specific rules don't apply
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="sla-hours">Standard SLA (hours)</Label>
                      <Input
                        id="sla-hours"
                        type="number"
                        min={24}
                        value={formState.sla_hours_default}
                        onChange={e => setFormState(prev => ({ ...prev, sla_hours_default: parseInt(e.target.value) || 336 }))}
                      />
                      <p className="text-sm text-muted-foreground">
                        Default: 336 hours (14 days)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="urgent-sla">Urgent SLA (hours)</Label>
                      <Input
                        id="urgent-sla"
                        type="number"
                        min={4}
                        value={formState.urgent_sla_hours_default}
                        onChange={e => setFormState(prev => ({ ...prev, urgent_sla_hours_default: parseInt(e.target.value) || 72 }))}
                      />
                      <p className="text-sm text-muted-foreground">
                        Default: 72 hours (3 days)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="follow-up">Follow-up Cadence (hours)</Label>
                      <Input
                        id="follow-up"
                        type="number"
                        min={4}
                        value={formState.follow_up_cadence_hours_default}
                        onChange={e => setFormState(prev => ({ ...prev, follow_up_cadence_hours_default: parseInt(e.target.value) || 48 }))}
                      />
                      <p className="text-sm text-muted-foreground">
                        Default: 48 hours (2 days)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle>Security & Privacy Controls</CardTitle>
                  <CardDescription>
                    PHI minimization and access logging settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>PHI Masking Enabled</Label>
                      <p className="text-sm text-muted-foreground">
                        Mask patient names, MRNs, and DOBs in lists and exports by default
                      </p>
                    </div>
                    <Switch
                      checked={formState.phi_masking_enabled}
                      onCheckedChange={v => setFormState(prev => ({ ...prev, phi_masking_enabled: v }))}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Sensitive Data Access Logging</Label>
                      <p className="text-sm text-muted-foreground">
                        Log when users view raw payloads or unmasked PHI
                      </p>
                    </div>
                    <Switch
                      checked={formState.sensitive_data_logging_enabled}
                      onCheckedChange={v => setFormState(prev => ({ ...prev, sensitive_data_logging_enabled: v }))}
                    />
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-500">Security Note</p>
                      <p className="text-sm text-muted-foreground">
                        All configuration changes are logged to the immutable audit trail. 
                        Disabling these controls will be recorded.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="exports">
              <Card>
                <CardHeader>
                  <CardTitle>Export Permissions</CardTitle>
                  <CardDescription>
                    Control which roles can export data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Hospital Admin Can Export</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow Hospital Admin users to generate and download reports
                      </p>
                    </div>
                    <Switch
                      checked={formState.hospital_admin_can_export}
                      onCheckedChange={v => setFormState(prev => ({ ...prev, hospital_admin_can_export: v }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Save Button */}
        {selectedClientId && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isUpdating}>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </div>
        )}
        </div>
      </div>
    </OpsLayout>
  );
}
